import clsx from 'clsx';
import type { HTMLAttributes, JSX } from 'react';
import type { ContextualColor, InterfaceColor } from './colors';
import type { Size } from './layout';

export interface RadioButtonOption<T> {
  value: T;
  isDisabled?: boolean;
  label?: string;
}

export interface RadioButtonGroupProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: RadioButtonOption<T>[];
  color?: InterfaceColor | ContextualColor;
  isDisabled?: boolean;
  selectedValue?: T;
  size?: Size;
  onChange?: (newValue: T) => void;
}

export function RadioButtonGroup<T>({
  options,
  className,
  color = 'primary',
  isDisabled = false,
  selectedValue,
  size = 'md',
  onChange = () => undefined,
  ...divProps
}: Readonly<RadioButtonGroupProps<T>>): JSX.Element {
  const innerOnClick = (value: T) => () => {
    onChange(value);
  };

  return (
    <div className={clsx(`btn-group btn-group-toggle btn-group-${size}`, className)} {...divProps}>
      {options.map(({ label, value, isDisabled: isOptionDisabled }) => (
        <button
          type="button"
          key={`${label}:${value}`}
          className={clsx(`btn btn-toggle-${color}`, {
            active: selectedValue === value,
          })}
          onClick={innerOnClick(value)}
          disabled={isDisabled || isOptionDisabled}
        >
          {label ?? String(value)}
        </button>
      ))}
    </div>
  );
}
